import {Splitter} from "@mytour/ui/layout";
import {toast} from "react-toastify";
import {Button, Card, CardBody, Spinner} from "reactstrap";
import {useUpdateEinvoicingChoice} from "../query/einvoicingQueries.js";
import {usePennylaneRecapModal} from "./PennylaneRecapModalContext.js";

type Props = {
  // Callback additionnel après un opt-out réussi (ex. le nudge #650 se
  // referme) — la section Facturation n'en a pas besoin, le cache React
  // Query invalidé par `useUpdateEinvoicingChoice` suffit à la faire
  // basculer sur l'état opt-out.
  onOptOut?: () => void;
  // Callback additionnel au clic "Commencer", avant l'ouverture de la
  // Récap (ex. le nudge #650 se referme pour laisser place à la Récap,
  // les deux modales globales ne peuvent pas rester ouvertes ensemble).
  onStartPennylane?: () => void;
  /**
   * Fourni seulement par les hôtes modale (le nudge #650) : l'échec d'opt-out
   * s'affiche alors inline dans la modale plutôt qu'en toast (la modale est la
   * surface UI active, doctrine composant d'action de `docs/conventions/frontend.md`).
   * Sans callback (écran de choix de la section Facturation, pas de modale), le
   * comportement par défaut reste le toast.
   */
  onOptOutError?: (message: string) => void;
};

/**
 * Deux callouts du choix binaire pennylane/opt-out — composant PARTAGÉ entre
 * l'écran de choix de la section Facturation et le nudge #650
 * (libellés canoniques figés le 05/08, cf. #651). Même gabarit visuel pour
 * les deux blocs, la hiérarchie se joue uniquement via le rôle du bouton.
 */
export function PennylaneChoiceCallouts({onOptOut, onStartPennylane, onOptOutError}: Props) {
  const {updateEinvoicingChoice, isUpdatingEinvoicingChoice} = useUpdateEinvoicingChoice();
  const {openRecapModal} = usePennylaneRecapModal();

  return (
    <Splitter>
      <Card className="h-100">
        <CardBody className="d-flex flex-column">
          <div className="fw-bold mb-1">Vous êtes déjà en règle</div>
          <p className="text-muted small flex-grow-1">Compte plateforme agréée déjà créé.</p>
          <Button
            color="secondary"
            outline
            className="align-self-center"
            disabled={isUpdatingEinvoicingChoice}
            onClick={handleOptOut}
          >
            Je suis déjà en règle
            {isUpdatingEinvoicingChoice && <Spinner size="sm" className="ms-2" />}
          </Button>
        </CardBody>
      </Card>
      <Card className="h-100">
        <CardBody className="d-flex flex-column">
          <div className="fw-bold mb-1">Créer un compte Pennylane</div>
          <p className="text-muted small flex-grow-1">
            Gratuit. MyTour crée le compte, vous le finalisez en quelques minutes. Vos factures
            restent gérées ici.
          </p>
          {/* Désactivé tant que l'opt-out est en vol : les deux choix sont mutuellement
              exclusifs côté serveur, un clic concurrent pendant la mutation opt-out
              pourrait ouvrir la création sur un choix qui vient de changer. */}
          <Button
            color="primary"
            className="align-self-center"
            disabled={isUpdatingEinvoicingChoice}
            onClick={handleStartPennylane}
          >
            Commencer
          </Button>
        </CardBody>
      </Card>
    </Splitter>
  );

  async function handleOptOut() {
    try {
      await updateEinvoicingChoice({choice: "external"});
      onOptOut?.();
    } catch {
      const message = "Impossible d'enregistrer votre choix";
      if (onOptOutError) {
        onOptOutError(message);
      } else {
        toast.error(message);
      }
    }
  }

  function handleStartPennylane() {
    onStartPennylane?.();
    openRecapModal();
  }
}
